import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useSelector } from 'react-redux';
import { FinancialDocumentPdfTemplateType } from '@mews-commander/data-access';
import { isCurrentEnterpriseBillingTemplatesEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { LocalizedText } from '@mews/framework';
import { createHandler } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { DropdownItem } from '@optimus-web/core';
import { SelectField, SelectFieldProps } from '../reduxFields';

const defaultFinancialDocumentTemplateSelectFieldProps = {
    name: 'template',
    label: <LocalizedText textKey={TextKey.BillFormat} />,
};

const getPrimaryText = createHandler<FinancialDocumentPdfTemplateType, React.ReactNode>(
    {
        [FinancialDocumentPdfTemplateType.Detailed]: <LocalizedText textKey={TextKey.DetailedInvoice} />,
        [FinancialDocumentPdfTemplateType.Consumption]: <LocalizedText textKey={TextKey.OverviewByDate} />,
        [FinancialDocumentPdfTemplateType.Reservation]: <LocalizedText textKey={TextKey.OverviewByReservation} />,
        [FinancialDocumentPdfTemplateType.OrderItem]: <LocalizedText textKey={TextKey.GeneralOverview} />,
        [FinancialDocumentPdfTemplateType.Guest]: <LocalizedText textKey={TextKey.BillFormatOverviewByGuest} />,
    },
    <LocalizedText textKey={TextKey.DetailedInvoice} />
);

const getSecondaryText = createHandler<FinancialDocumentPdfTemplateType, React.ReactNode>(
    {
        [FinancialDocumentPdfTemplateType.Detailed]: <LocalizedText textKey={TextKey.DetailedInvoiceDescription} />,
        [FinancialDocumentPdfTemplateType.Consumption]: <LocalizedText textKey={TextKey.OverviewByDateDescription} />,
        [FinancialDocumentPdfTemplateType.Reservation]: (
            <LocalizedText textKey={TextKey.OverviewByReservationDescription} />
        ),
        [FinancialDocumentPdfTemplateType.OrderItem]: <LocalizedText textKey={TextKey.GeneralOverviewDescription} />,
        [FinancialDocumentPdfTemplateType.Guest]: (
            <LocalizedText textKey={TextKey.BillFormatOverviewByGuestDescription} />
        ),
    },
    <LocalizedText textKey={TextKey.DetailedInvoiceDescription} />
);

const getTemplateOptions = (allowedTemplateTypes: Array<FinancialDocumentPdfTemplateType>) =>
    pipe(
        allowedTemplateTypes,
        array.map((template) => (
            <DropdownItem
                key={template}
                value={template}
                label={getPrimaryText(template)}
                description={getSecondaryText(template)}
            />
        ))
    );

export const FinancialDocumentTemplateSelectField = (
    props: SelectFieldProps & { allowedTemplateTypes: Array<FinancialDocumentPdfTemplateType> }
) => {
    const isBillingTemplatesEnabled: boolean = useSelector(isCurrentEnterpriseBillingTemplatesEnabled);
    if (!isBillingTemplatesEnabled) {
        return null;
    }
    return (
        <SelectField {...defaultFinancialDocumentTemplateSelectFieldProps} {...props}>
            {getTemplateOptions(props.allowedTemplateTypes)}
        </SelectField>
    );
};
